import { useState, useEffect } from "react";
import { API } from "../../App";
import { Gift, Star, Check, ShoppingBag, Ticket, Wine } from "lucide-react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "react-i18next";

export default function CustomerRewards() {
  const { t } = useTranslation();
  const [catalog, setCatalog] = useState([]);
  const [points, setPoints] = useState(0);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(null);
  const [tab, setTab] = useState("catalog");

  const fetchData = async () => {
    try {
      const [catRes, histRes] = await Promise.all([
        axios.get(`${API}/rewards/catalog`),
        axios.get(`${API}/rewards/history`),
      ]);
      setCatalog(catRes.data.rewards);
      setPoints(catRes.data.available_points);
      setHistory(histRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleRedeem = async (rewardId) => {
    setRedeeming(rewardId);
    try {
      const { data } = await axios.post(`${API}/rewards/redeem`, { reward_id: rewardId });
      toast.success(`Redeemed! Your code: ${data.code}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Redemption failed");
    } finally {
      setRedeeming(null);
    }
  };

  const typeIcons = {
    perk: Star,
    discount: Ticket,
    experience: Wine,
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse bg-muted h-32 rounded-xl" />)}
      </div>
    );
  }

  return (
    <div data-testid="customer-rewards-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-medium text-foreground">{t("customer_loyalty:redeem")}</h1>
        <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
          <Gift className="h-4 w-4 text-primary" />
          <span className="text-sm font-semibold text-primary" data-testid="rewards-points-balance">{points} pts</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={tab === "catalog" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("catalog")}
          data-testid="rewards-catalog-tab"
        >
          <ShoppingBag className="h-4 w-4 mr-1" />
          Catalog
        </Button>
        <Button
          variant={tab === "history" ? "default" : "outline"}
          size="sm"
          onClick={() => setTab("history")}
          data-testid="rewards-history-tab"
        >
          <Check className="h-4 w-4 mr-1" />
          My Rewards ({history.length})
        </Button>
      </div>

      {tab === "catalog" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {catalog.map((reward) => {
            const Icon = typeIcons[reward.type] || Gift;
            return (
              <div
                key={reward.reward_id}
                className={`bg-card rounded-xl border p-5 transition-all ${reward.can_redeem ? "border-primary/30 hover:border-primary" : "border-border opacity-70"}`}
                data-testid={`reward-${reward.reward_id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`p-2.5 rounded-xl ${reward.can_redeem ? "bg-primary/10" : "bg-muted"}`}>
                    <Icon className={`h-5 w-5 ${reward.can_redeem ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-foreground">{reward.name}</h3>
                    <p className="text-sm text-muted-foreground mt-0.5">{reward.description}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="text-lg font-semibold text-foreground">{reward.points_cost} <span className="text-sm text-muted-foreground font-normal">pts</span></span>
                  <Button
                    size="sm"
                    disabled={!reward.can_redeem || redeeming === reward.reward_id}
                    onClick={() => handleRedeem(reward.reward_id)}
                    data-testid={`redeem-btn-${reward.reward_id}`}
                  >
                    {redeeming === reward.reward_id ? "Redeeming..." : reward.can_redeem ? "Redeem" : "Not enough pts"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === "history" && (
        <div>
          {history.length > 0 ? (
            <div className="space-y-3">
              {history.map((r) => (
                <div key={r.redemption_id} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4" data-testid={`redemption-${r.redemption_id}`}>
                  <div className="p-2.5 bg-green-100 rounded-xl">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">{r.reward_name}</p>
                    <p className="text-sm text-muted-foreground">{r.created_at?.split("T")[0]}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm font-semibold bg-muted px-3 py-1 rounded-lg" data-testid={`redemption-code-${r.redemption_id}`}>{r.code}</p>
                    <p className="text-xs text-muted-foreground mt-1">-{r.points_spent} pts</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-card rounded-xl border border-border">
              <Gift className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No rewards redeemed yet</h3>
              <p className="text-muted-foreground">Browse the catalog and redeem your first reward!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
